import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Droplets, Search, Printer, Download, Gift, Car, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, peso, apiError, STATUS_STEPS, STATUS_META } from "../../lib/api";
import { Card, Btn, Field, LiveTimer, StatusBadge, Money, cx, useInterval } from "../../components/kit";

const VEHICLE_TYPES = ["Sedan", "Hatchback", "SUV", "Pickup", "Van", "Motorcycle", "Other"];
const PAY_METHODS = ["Cash", "GCash", "Maya", "Debit Card", "Credit Card", "Bank Transfer"];

export default function CustomerApp() {
  const [step, setStep] = useState("identify");
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [order, setOrder] = useState(null);

  const Header = () => (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-cyan-500 flex items-center justify-center shadow-[0_0_20px_rgba(0,210,254,0.4)]">
        <Droplets className="text-slate-950" size={22} strokeWidth={2.6} />
      </div>
      <div>
        <h1 className="font-head text-lg font-extrabold leading-none">Cool Car Customer</h1>
        <p className="text-[0.65rem] text-cyan-400/90 font-bold tracking-widest uppercase">Service Portal</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-8 pb-28">
      <Header />
      {step === "identify" && <Identify setCustomer={setCustomer} setVehicle={setVehicle} setStep={setStep} />}
      {step === "register" && <Register setCustomer={setCustomer} setVehicle={setVehicle} setStep={setStep} />}
      {step === "service" && <ServiceSelect customer={customer} vehicle={vehicle} setVehicle={setVehicle} setOrder={setOrder} setStep={setStep} />}
      {step === "status" && <StatusScreen order={order} setOrder={setOrder} setStep={setStep} />}
      {step === "pay" && <PayScreen order={order} setOrder={setOrder} setStep={setStep} />}
      {step === "receipt" && <Receipt order={order} customer={customer} setStep={setStep} />}
      {step === "loyalty" && <Loyalty customer={customer} setStep={setStep} />}
    </div>
  );
}

function Identify({ setCustomer, setVehicle, setStep }) {
  const [mobile, setMobile] = useState("");
  const [plate, setPlate] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    if (!mobile && !plate) return toast.error("Enter your mobile or plate number");
    setLoading(true);
    try {
      const { data } = await api.post("/customers/lookup", { mobile, plate });
      if (data.found) {
        setCustomer(data.customer);
        setVehicle(data.matched_vehicle || data.customer.vehicles?.[0] || null);
        toast.success(`Welcome back, ${data.customer.name}!`);
        setStep("service");
      } else {
        toast.info("No profile found — let's register you");
        setStep("register");
      }
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  return (
    <Card className="animate-in">
      <h2 className="font-head text-xl font-bold mb-1">Welcome to Cool Car</h2>
      <p className="text-sm text-slate-400 mb-5">Enter your details to check in. Returning customers are recognized automatically.</p>
      <div className="space-y-3">
        <Field label="Mobile Number" testid="customer-lookup-mobile" placeholder="09XXXXXXXXX" value={mobile} onChange={(e) => setMobile(e.target.value)} />
        <Field label="Plate Number" testid="customer-lookup-plate" placeholder="ABC 1234" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} />
        <Btn data-testid="customer-lookup-btn" className="w-full flex items-center justify-center gap-2" onClick={lookup} disabled={loading}>
          <Search size={18} /> {loading ? "Checking..." : "Continue"}
        </Btn>
        <button data-testid="customer-new-btn" className="w-full text-sm text-cyan-400 font-semibold py-2" onClick={() => setStep("register")}>
          New here? Register a new customer
        </button>
      </div>
    </Card>
  );
}

function Register({ setCustomer, setVehicle, setStep }) {
  const [f, setF] = useState({ name: "", mobile: "", email: "", birthday: "", plate: "", vehicle_type: "Sedan", make: "", model: "", color: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name || !f.mobile || !f.plate) return toast.error("Name, mobile and plate are required");
    setLoading(true);
    try {
      const { data } = await api.post("/customers", {
        name: f.name, mobile: f.mobile, email: f.email, birthday: f.birthday,
        vehicle: { plate: f.plate, vehicle_type: f.vehicle_type, make: f.make, model: f.model, color: f.color },
      });
      setCustomer(data);
      setVehicle(data.vehicles?.[0] || null);
      toast.success("Profile created!");
      setStep("service");
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  return (
    <Card className="animate-in">
      <button className="text-slate-400 text-sm flex items-center gap-1 mb-3" onClick={() => setStep("identify")}><ArrowLeft size={16} /> Back</button>
      <h2 className="font-head text-xl font-bold mb-4">New Customer</h2>
      <div className="space-y-3">
        <Field label="Full Name *" testid="reg-name" value={f.name} onChange={set("name")} />
        <Field label="Mobile Number *" testid="reg-mobile" value={f.mobile} onChange={set("mobile")} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email" testid="reg-email" value={f.email} onChange={set("email")} />
          <Field label="Birthday" type="date" testid="reg-birthday" value={f.birthday} onChange={set("birthday")} />
        </div>
        <div className="pt-2 border-t border-slate-700/50">
          <div className="ccc-label mt-2 flex items-center gap-1"><Car size={12} /> Vehicle Info</div>
        </div>
        <Field label="Plate Number *" testid="reg-plate" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} />
        <label className="block">
          <span className="ccc-label">Vehicle Type</span>
          <select className="ccc-input" data-testid="reg-vtype" value={f.vehicle_type} onChange={set("vehicle_type")}>
            {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Make" testid="reg-make" value={f.make} onChange={set("make")} />
          <Field label="Model" testid="reg-model" value={f.model} onChange={set("model")} />
        </div>
        <Field label="Color" testid="reg-color" value={f.color} onChange={set("color")} />
        <Btn className="w-full" data-testid="reg-submit" onClick={submit} disabled={loading}>{loading ? "Saving..." : "Create Profile & Continue"}</Btn>
      </div>
    </Card>
  );
}

function ServiceSelect({ customer, vehicle, setVehicle, setOrder, setStep }) {
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [svc, setSvc] = useState(null);
  const [picked, setPicked] = useState({});
  const [quote, setQuote] = useState(null);
  const [redeem, setRedeem] = useState(0);
  const [placing, setPlacing] = useState(false);
  const vehicles = customer?.vehicles || (vehicle ? [vehicle] : []);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/addons").then((r) => setAddons(r.data));
  }, []);

  const addonIds = useMemo(() => Object.keys(picked).filter((k) => picked[k]), [picked]);

  useEffect(() => {
    if (!svc || !vehicle) { setQuote(null); return; }
    api.post("/pricing/quote", { service_id: svc.id, vehicle_type: vehicle.vehicle_type, addon_ids: addonIds, loyalty_points: Number(redeem) || 0 })
      .then((r) => setQuote(r.data)).catch(() => {});
  }, [svc, vehicle, addonIds, redeem]);

  const confirm = async () => {
    if (!svc || !vehicle) return toast.error("Select a service");
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        customer_id: customer.id, vehicle_id: vehicle.id, service_id: svc.id,
        addon_ids: addonIds, loyalty_points: Number(redeem) || 0, source: "customer",
      });
      setOrder(data);
      toast.success(`Order ${data.transaction_no} confirmed!`);
      setStep("status");
    } catch (e) { toast.error(apiError(e)); }
    setPlacing(false);
  };

  return (
    <div className="animate-in space-y-4">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400">Customer</div>
            <div className="font-bold">{customer?.name}</div>
          </div>
          <StatusBadge status="waiting" />
        </div>
        {vehicles.length > 0 && (
          <div className="mt-3">
            <span className="ccc-label">Vehicle</span>
            <select className="ccc-input" data-testid="cust-vehicle-select" value={vehicle?.id || ""}
              onChange={(e) => setVehicle(vehicles.find((v) => v.id === e.target.value))}>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate} · {v.vehicle_type} · {v.make} {v.model}</option>)}
            </select>
          </div>
        )}
      </Card>

      <div>
        <div className="ccc-label mb-2">Choose a Service</div>
        <div className="space-y-2">
          {services.map((s) => (
            <button key={s.id} data-testid={`service-${s.id}`} onClick={() => setSvc(s)}
              className={cx("w-full text-left rounded-xl p-3.5 border transition-all",
                svc?.id === s.id ? "bg-cyan-500/10 border-cyan-400/60" : "bg-[#1C2541] border-slate-700/60")}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-xs text-slate-400">{s.description} · ~{s.duration_min}min</div>
                </div>
                <div className="mono text-cyan-400 font-bold">{vehicle ? peso(s.prices?.[vehicle.vehicle_type] ?? s.base_price) : ""}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="ccc-label mb-2">Add-ons</div>
        <div className="grid grid-cols-2 gap-2">
          {addons.map((a) => (
            <button key={a.id} data-testid={`addon-${a.id}`} onClick={() => setPicked({ ...picked, [a.id]: !picked[a.id] })}
              className={cx("rounded-xl p-3 border text-left transition-all",
                picked[a.id] ? "bg-cyan-500/10 border-cyan-400/60" : "bg-[#1C2541] border-slate-700/60")}>
              <div className="text-sm font-medium">{a.name}</div>
              <div className="mono text-xs text-cyan-400">{peso(a.price)}</div>
            </button>
          ))}
        </div>
      </div>

      {(customer?.points > 0) && (
        <Card>
          <span className="ccc-label">Redeem Loyalty Points (you have {customer.points})</span>
          <input type="number" min="0" max={customer.points} className="ccc-input" data-testid="cust-redeem" value={redeem} onChange={(e) => setRedeem(e.target.value)} />
        </Card>
      )}

      {quote && (
        <Card className="border-cyan-500/30">
          <div className="ccc-label mb-2">Order Summary</div>
          <Row k={quote.service.name} v={peso(quote.service.price)} />
          {quote.addons.map((a) => <Row key={a.id} k={`+ ${a.name}`} v={peso(a.price)} sub />)}
          <div className="border-t border-slate-700/50 my-2" />
          <Row k="Subtotal" v={peso(quote.subtotal)} />
          {quote.discount > 0 && <Row k="Discount" v={`- ${peso(quote.discount)}`} />}
          {quote.loyalty_redeemed_value > 0 && <Row k="Loyalty Redemption" v={`- ${peso(quote.loyalty_redeemed_value)}`} />}
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-700/50">
            <span className="font-head font-bold">Total Due</span>
            <span data-testid="cust-total" className="mono text-2xl font-extrabold text-cyan-400">{peso(quote.total)}</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">Earns {quote.points_earned} loyalty points</div>
        </Card>
      )}

      <Btn className="w-full" data-testid="cust-confirm-order" onClick={confirm} disabled={!svc || placing}>
        {placing ? "Placing..." : "Confirm Order"}
      </Btn>
    </div>
  );
}

function Row({ k, v, sub }) {
  return (
    <div className={cx("flex justify-between text-sm py-0.5", sub && "text-slate-400")}>
      <span>{k}</span><span className="mono">{v}</span>
    </div>
  );
}

function StatusScreen({ order, setOrder, setStep }) {
  const [, tick] = useState(0);
  useInterval(async () => {
    try { const { data } = await api.get(`/orders/${order.id}`); setOrder(data); tick((x) => x + 1); } catch {}
  }, 4000);

  const idx = STATUS_STEPS.indexOf(order.status);
  const timerStart = order.timers?.service_start;

  return (
    <div className="animate-in space-y-4">
      <Card className="border-cyan-500/30">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-xs text-slate-400">Transaction</div>
            <div data-testid="cust-txno" className="mono font-bold text-cyan-400">{order.transaction_no}</div>
          </div>
          <StatusBadge status={order.status} testid="cust-status-badge" />
        </div>
        <div className="mt-3 text-sm">{order.vehicle.plate} · {order.vehicle.type} · {order.service.name}</div>
        <div className="flex items-center gap-3 mt-3">
          {order.bay_id && <span className="text-sm">Bay: <b className="text-cyan-400">assigned</b></span>}
          {timerStart && <LiveTimer from={timerStart} to={order.timers?.service_finish} targetMin={order.service?.duration_min} testid="cust-timer" />}
        </div>
      </Card>

      <Card>
        <div className="ccc-label mb-3">Live Progress</div>
        <div className="space-y-2.5">
          {STATUS_STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <div className={cx("w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 border",
                i < idx ? "bg-cyan-500 border-cyan-500 text-slate-950" : i === idx ? "bg-cyan-500/20 border-cyan-400 text-cyan-400 glow-pulse" : "bg-transparent border-slate-700 text-slate-600")}>
                {i < idx ? <CheckCircle2 size={14} /> : i + 1}
              </div>
              <span className={cx("text-sm", i <= idx ? "text-slate-100 font-medium" : "text-slate-500")}>{STATUS_META[s].label}</span>
            </div>
          ))}
        </div>
      </Card>

      <Btn className="w-full" data-testid="cust-goto-pay" onClick={() => setStep("pay")}
        variant={order.payment_status === "paid" ? "success" : "primary"}>
        {order.payment_status === "paid" ? "View Receipt & Pay Status" : "Pay Now"}
      </Btn>
    </div>
  );
}

function PayScreen({ order, setOrder, setStep }) {
  const [method, setMethod] = useState("GCash");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const due = Math.max(0, order.total - (order.amount_paid || 0));

  const pay = async () => {
    if (method !== "Cash" && !reference) return toast.error("Enter a payment reference number");
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/customer-payment`, { amount: due, method, reference });
      const { data } = await api.get(`/orders/${order.id}`);
      setOrder(data);
      toast.success("Payment recorded!");
      setStep("receipt");
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  if (order.payment_status === "paid") {
    return (
      <Card className="animate-in text-center">
        <CheckCircle2 className="mx-auto text-emerald-400 mb-2" size={44} />
        <div className="font-head text-xl font-bold">Already Paid</div>
        <Btn className="w-full mt-4" onClick={() => setStep("receipt")}>View Receipt</Btn>
      </Card>
    );
  }

  return (
    <div className="animate-in space-y-4">
      <button className="text-slate-400 text-sm flex items-center gap-1" onClick={() => setStep("status")}><ArrowLeft size={16} /> Back to status</button>
      <Card>
        <div className="text-xs text-slate-400">Amount Due</div>
        <div className="mono text-3xl font-extrabold text-cyan-400" data-testid="pay-due">{peso(due)}</div>
      </Card>
      <div>
        <div className="ccc-label mb-2">Payment Method</div>
        <div className="grid grid-cols-3 gap-2">
          {PAY_METHODS.map((m) => (
            <button key={m} data-testid={`pay-method-${m}`} onClick={() => setMethod(m)}
              className={cx("rounded-xl p-2.5 text-xs font-semibold border", method === m ? "bg-cyan-500 text-slate-950 border-cyan-400" : "bg-[#1C2541] border-slate-700 text-slate-300")}>{m}</button>
          ))}
        </div>
      </div>
      {method !== "Cash" && <Field label="Reference Number" testid="pay-reference" placeholder="e.g. 0091234567" value={reference} onChange={(e) => setReference(e.target.value)} />}
      <Btn className="w-full" data-testid="pay-submit" onClick={pay} disabled={loading}>{loading ? "Processing..." : `Pay ${peso(due)}`}</Btn>
      <p className="text-xs text-slate-500 text-center">Electronic payments are confirmed via reference number for this MVP.</p>
    </div>
  );
}

function Receipt({ order, customer, setStep }) {
  const [full, setFull] = useState(order);
  useEffect(() => { api.get(`/orders/${order.id}`).then((r) => setFull(r.data)); }, [order.id]);

  const download = () => {
    const html = document.getElementById("receipt-body").innerHTML;
    const blob = new Blob([`<html><body style="font-family:monospace;padding:24px">${html}</body></html>`], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${order.transaction_no}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-in space-y-4">
      <Card className="print-area">
        <div id="receipt-body">
          <div className="text-center mb-3">
            <div className="font-head font-extrabold text-lg">COOL CAR CENTRALE</div>
            <div className="text-xs text-slate-400">Official Receipt</div>
          </div>
          <Row k="Transaction" v={full.transaction_no} />
          <Row k="Date" v={new Date(full.created_at).toLocaleString()} />
          <Row k="Customer" v={full.customer_name} />
          <Row k="Vehicle" v={`${full.vehicle.plate} · ${full.vehicle.type}`} />
          <div className="border-t border-slate-700/50 my-2" />
          <Row k={full.service.name} v={peso(full.service.price)} />
          {full.addons?.map((a) => <Row key={a.id} k={`+ ${a.name}`} v={peso(a.price)} sub />)}
          <div className="border-t border-slate-700/50 my-2" />
          <Row k="Subtotal" v={peso(full.subtotal)} />
          {full.discount > 0 && <Row k="Discount" v={`- ${peso(full.discount)}`} />}
          {full.loyalty_redeemed_value > 0 && <Row k="Loyalty" v={`- ${peso(full.loyalty_redeemed_value)}`} />}
          <div className="flex justify-between font-bold mt-1"><span>TOTAL PAID</span><span className="mono">{peso(full.total)}</span></div>
          <Row k="Payment" v={(full.payments || []).map((p) => p.method).join(", ") || "—"} />
          <Row k="Points Earned" v={`+${full.points_earned}`} />
          <div className="text-center text-xs text-slate-400 mt-3">Thank you for choosing Cool Car! ✨</div>
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-2 no-print">
        <Btn variant="ghost" data-testid="receipt-print" onClick={() => window.print()} className="flex items-center justify-center gap-2"><Printer size={16} /> Print</Btn>
        <Btn variant="ghost" data-testid="receipt-download" onClick={download} className="flex items-center justify-center gap-2"><Download size={16} /> Download</Btn>
      </div>
      <Btn className="w-full no-print flex items-center justify-center gap-2" data-testid="receipt-loyalty" onClick={() => setStep("loyalty")}><Gift size={16} /> View My Loyalty</Btn>
    </div>
  );
}

function Loyalty({ customer, setStep }) {
  const [c, setC] = useState(customer);
  useEffect(() => { api.get(`/customers/${customer.id}/public`).then((r) => setC(r.data)); }, [customer.id]);
  const stamps = Math.min(c.visits || 0, c.free_service_threshold || 10);

  return (
    <div className="animate-in space-y-4">
      <Card className="border-cyan-500/30 text-center">
        <div className="inline-flex px-3 py-1 rounded-full bg-cyan-500/15 text-cyan-400 text-xs font-bold tracking-widest uppercase mb-2">{c.status} Member</div>
        <div className="font-head text-xl font-bold">{c.name}</div>
        <div className="mono text-4xl font-extrabold text-cyan-400 mt-2" data-testid="loyalty-points">{c.points}</div>
        <div className="text-xs text-slate-400">Points Available</div>
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <Card><div className="text-xs text-slate-400">Total Visits</div><div className="mono text-xl font-bold">{c.visits}</div></Card>
        <Card><div className="text-xs text-slate-400">Lifetime Spend</div><div className="mono text-xl font-bold">{peso(c.lifetime_spend)}</div></Card>
        <Card><div className="text-xs text-slate-400">Avg Ticket</div><div className="mono text-xl font-bold">{peso(c.avg_ticket)}</div></Card>
        <Card><div className="text-xs text-slate-400">Last Visit</div><div className="text-sm font-medium">{c.last_visit ? new Date(c.last_visit).toLocaleDateString() : "—"}</div></Card>
      </div>
      <Card>
        <div className="ccc-label mb-2">Wash Stamp Card — {c.free_service_threshold} washes = 1 FREE</div>
        <div className="grid grid-cols-5 gap-2">
          {Array.from({ length: c.free_service_threshold || 10 }).map((_, i) => (
            <div key={i} className={cx("aspect-square rounded-lg flex items-center justify-center border", i < stamps ? "bg-cyan-500/20 border-cyan-400 text-cyan-400" : "border-slate-700 text-slate-600")}>
              <Droplets size={16} />
            </div>
          ))}
        </div>
      </Card>
      <Btn className="w-full" onClick={() => setStep("identify")}>Done</Btn>
    </div>
  );
}
